// Publication 게시판 데이터 (atlas_research_publications.html 에서 옮김). 그림은 public/img/publications/<id>/
export interface PublicationFigure {
  label: string;
  caption: string;
  image: string;
}

export interface Publication {
  id: string;
  year: number;
  // 화면에 그대로 보이는 게재일 표기
  date: string;
  title: string;
  authors: string;
  journal: string;
  summary: string;
  url: string;
  // 아틀라스 과제 지원 표기
  support: string;
  figures: PublicationFigure[];
}

export const PUBLICATION_HERO = {
  "eyebrow": "Research / 2024–2026",
  "title": "Atlas Research",
  "subtitle": "Publications acknowledging support from the Pediatric Rare Disease Pathology Atlas · 2024–2026",
  "acknowledgment": "The publications below acknowledge support from the SNUH Lee Kun-hee Child Cancer & Rare Disease Project, Republic of Korea (Grant Nos. 24C-039-0000, 25C-039-0000, and 26C-039-0000)."
};

export const PUBLICATION_FOOTNOTE = "Pediatric Rare Disease Pathology Atlas · Research collection · Updated October 31, 2026";

export const PUBLICATIONS: Publication[] = [
  {
    "id": "2024-ntrk-cns",
    "year": 2024,
    "date": "July 16, 2024",
    "title": "NTRK-fused central nervous system tumours: clinicopathological and genetic insights and response to TRK inhibitors",
    "authors": "Eric Eunshik Kim et al.",
    "journal": "Acta Neuropathologica Communications · 12:118",
    "summary": "Clinical, pathological and genetic characterization of NTRK-fused CNS tumours and their response to TRK inhibitors.",
    "url": "https://doi.org/10.1186/s40478-024-01798-9",
    "support": "Atlas support · 24C-039-0000",
    "figures": [
      {
        "label": "Figure 1",
        "caption": "A, B Patient #2 (15 y/F) presented with an intraventricular diffuse low-grade glioma located at the lateral ventricle, exhibiting heterogeneous high signal intensity on contrast-enhanced T1-weighted and T1-fluid-attenuated inversion recover.",
        "image": "/img/publications/2024-ntrk-cns/fig1.png"
      },
      {
        "label": "Figure 2",
        "caption": "Patient #5, a 27-year-old female, presented with an uncommon glioma harbouring the LHFPL3::NTRK2 fusion.",
        "image": "/img/publications/2024-ntrk-cns/fig2.png"
      },
      {
        "label": "Figure 3",
        "caption": "Patient #12 had the BCR::NTRK2 fusion.",
        "image": "/img/publications/2024-ntrk-cns/fig3.png"
      },
      {
        "label": "Figure 4",
        "caption": "Illustration of the clinicopathological and genetic abnormalities in NTRK-fused gliomas in paediatric and adult patients, Abbreviations LG, low-grade; HG, high-grade; P, parietal lobe; LV, lateral ventricle; Thal, thalamus, TO, temporo-occi.",
        "image": "/img/publications/2024-ntrk-cns/fig4.png"
      },
      {
        "label": "Figure 5",
        "caption": "Summary of the radiological characteristics and treatment timelines for patients receiving TRK inhibitor therapy (IHG: Infant-type hemispheric glioma, DLGG: Diffuse low-grade glioma, DHGG: Diffuse high-grade glioma, GBM: Glioblastoma, IDH-W.",
        "image": "/img/publications/2024-ntrk-cns/fig5.png"
      }
    ]
  },
  {
    "id": "2025-mpfd-proteome",
    "year": 2025,
    "date": "May 30, 2025",
    "title": "Plasma proteome profiling of massive perivillous fibrin deposition (MPFD) in women with recurrent Miscarriages: Pathophysiological insights and biomarker discovery",
    "authors": "Eun Na Kim et al.",
    "journal": "Placenta",
    "summary": "Plasma proteomic profiling of MPFD in women with recurrent miscarriages, exploring disease mechanisms and candidate biomarkers.",
    "url": "https://doi.org/10.1016/j.placenta.2025.05.027",
    "support": "Atlas support · 25C-039-0000",
    "figures": [
      {
        "label": "Figure 1",
        "caption": "Overview of Proteomic Profiling and Differential Expression Analysis in the f-MPFD, MC, and NC Groups (A) Venn diagram of 833 proteins identified from the f-MPFD, MC, and NC groups.",
        "image": "/img/publications/2025-mpfd-proteome/fig1.png"
      },
      {
        "label": "Figure 2",
        "caption": "Pathway Enrichment Analysis of Proteins Consistently Dysregulated in f-MPFD Pathway enrichment analysis was performed using Metascape (htt ps://metascape.org) on proteins that were consistently upregulated (right panel) or downregulated (le.",
        "image": "/img/publications/2025-mpfd-proteome/fig2.png"
      },
      {
        "label": "Figure 3",
        "caption": "Identification of Candidate Biomarker Proteins in f-MPFD Using Partial Least Squares Discriminant Analysis (PLS-DA) (A) Three-dimensional PLS-DA plot based on log2-transformed protein abundance profiles showing clear separation among the th.",
        "image": "/img/publications/2025-mpfd-proteome/fig3.png"
      },
      {
        "label": "Figure 4",
        "caption": "Hierarchical Clustering and Functional Enrichment of Differentially Expressed Proteins Across NC, MC, and f-MPFD Groups (A) Hierarchical clustering of 265 proteins that were significantly differentially expressed among the three study group.",
        "image": "/img/publications/2025-mpfd-proteome/fig4.png"
      }
    ]
  },
  {
    "id": "2026-stain-texture",
    "year": 2026,
    "date": "April 2, 2026",
    "title": "Texture feature analysis of stain-separated histopathology images",
    "authors": "Jae Eun Song, Kwangsoo Kim, Eun Na Kim",
    "journal": "Medical Imaging 2026: Digital and Computational Pathology · SPIE",
    "summary": "Stain-separated texture features for distinguishing chronic inflammation from fibrosis in histopathology images.",
    "url": "https://doi.org/10.1117/12.3088018",
    "support": "Atlas support · 26C-039-0000",
    "figures": [
      {
        "label": "Figure 1",
        "caption": "Visualization of stain separation for chronic inflammation sample.",
        "image": "/img/publications/2026-stain-texture/fig1.png"
      },
      {
        "label": "Figure 2",
        "caption": "Visualization of stain separation for fibrosis sample.",
        "image": "/img/publications/2026-stain-texture/fig2.png"
      },
      {
        "label": "Figure 3",
        "caption": "Comparison of Haralick texture features between chronic inflammation and fibrosis in stain-separated thoracic pathology images.",
        "image": "/img/publications/2026-stain-texture/fig3.png"
      },
      {
        "label": "Figure 4",
        "caption": "SVM classification results using Haralick texture features from stain-separated concentration maps.",
        "image": "/img/publications/2026-stain-texture/fig4.png"
      }
    ]
  },
  {
    "id": "2026-svg-enos",
    "year": 2026,
    "date": "August 13, 2026",
    "title": "Adaptive proteomic remodeling and eNOS upregulation in luminal endothelium and perivascular adipose tissue of patent saphenous vein grafts after CABG",
    "authors": "Eun Na Kim et al.",
    "journal": "Cardiovascular Pathology",
    "summary": "Proteomic remodeling and eNOS expression in patent saphenous vein grafts following coronary artery bypass grafting.",
    "url": "https://doi.org/10.1016/j.carpath.2026.107853",
    "support": "Atlas support · 26C-039-0000",
    "figures": [
      {
        "label": "Figure 1",
        "caption": "Structural remodeling of ITA and SVG bypass grafts.",
        "image": "/img/publications/2026-svg-enos/fig1.png"
      },
      {
        "label": "Figure 2",
        "caption": "Immunophenotypic identification of the saphenous vein graft neointimal cells.",
        "image": "/img/publications/2026-svg-enos/fig2.png"
      },
      {
        "label": "Figure 3",
        "caption": "Upregulation of luminal endothelial eNOS in patent grafts.",
        "image": "/img/publications/2026-svg-enos/fig3.png"
      },
      {
        "label": "Figure 4",
        "caption": "Proliferation of adventitial microvessels in patent grafts.",
        "image": "/img/publications/2026-svg-enos/fig4.png"
      },
      {
        "label": "Figure 5",
        "caption": "eNOS-positive perivascular microvessels in patent grafts.",
        "image": "/img/publications/2026-svg-enos/fig5.png"
      },
      {
        "label": "Figure 6",
        "caption": "Convergent proteomic adaptation of patent ITA and SVG grafts.",
        "image": "/img/publications/2026-svg-enos/fig6.png"
      },
      {
        "label": "Figure 7",
        "caption": "Convergent and graft-specific upstream regulators of graft adaptation.",
        "image": "/img/publications/2026-svg-enos/fig7.png"
      },
      {
        "label": "Figure 8",
        "caption": "Graphical summary of the study patients, methods, key finding (dual-compartment eNOS upregulation in patent grafts), and clinical implication for no-touch SVG harvesting..",
        "image": "/img/publications/2026-svg-enos/fig8.png"
      }
    ]
  },
  {
    "id": "2026-aml-mllt10",
    "year": 2026,
    "date": "September 10, 2026",
    "title": "Very Late Relapse of Acute Myeloid Leukemia With a Rare HNRNPH1::MLLT10 Fusion Following a Pediatric Myeloid Neoplasm: A Successful Venetoclax–Azacitidine Bridge to Hematopoietic Stem Cell Transplantation",
    "authors": "Gyuyeon Lee et al.",
    "journal": "Pediatric Blood & Cancer",
    "summary": "A rare HNRNPH1::MLLT10 fusion in very late AML relapse, with venetoclax–azacitidine used as a bridge to transplantation.",
    "url": "https://doi.org/10.1002/1545-5017.70659",
    "support": "Atlas support · 26C-039-0000",
    "figures": [
      {
        "label": "Figure 1",
        "caption": "Histopathologic findings of the initial myeloid sarcoma.",
        "image": "/img/publications/2026-aml-mllt10/fig1.png"
      }
    ]
  },
  {
    "id": "2026-mcda-twins",
    "year": 2026,
    "date": "2026 · Volume 58, Issue 3",
    "title": "Assessment of Placental Vascular Anastomoses and Villous Maturation Discordance: Associations with Birthweight Discordance in Monochorionic Diamniotic Twins",
    "authors": "HyunBeom Song, YunSeo Noh, HaeMin Yeo, SungYeon Jung, HoYeong Jang, Eun Na Kim",
    "journal": "Korean Journal of Clinical Laboratory Science · 58(3):321–331",
    "summary": "Placental vascular anastomoses and villous maturation discordance in relation to birthweight discordance in MCDA twins.",
    "url": "https://doi.org/10.15324/kjcls.2026.58.3.321",
    "support": "Atlas support · 26C-039-0000",
    "figures": [
      {
        "label": "Figure 1",
        "caption": "Flow diagram of study cohort selection.",
        "image": "/img/publications/2026-mcda-twins/fig1.png"
      },
      {
        "label": "Figure 2",
        "caption": "Representative gross findings of chorionic plate vascular anastomoses in monochorionic diamniotic placentas.",
        "image": "/img/publications/2026-mcda-twins/fig2.png"
      },
      {
        "label": "Figure 3",
        "caption": "Representative hematoxylin and eosin stained images of within-pair villous maturation discordance.",
        "image": "/img/publications/2026-mcda-twins/fig3.png"
      },
      {
        "label": "Figure 4",
        "caption": "Birthweight discordance according to the presence of AA/VV anastomosis.",
        "image": "/img/publications/2026-mcda-twins/fig4.png"
      },
      {
        "label": "Figure 5",
        "caption": "Birthweight discordance according to the degree of villous maturation discordance.",
        "image": "/img/publications/2026-mcda-twins/fig5.png"
      }
    ]
  }
];
